import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable, of, switchMap, tap } from 'rxjs';
import { Project } from 'src/app/interfaces/api/project';
import { QueryParams } from 'src/app/interfaces/shared/query';
import { ProjectService } from '../api/projects/project.service';
import { ApiService } from './api.service';
import { dbScopeFor } from './collection-scope.config';
import { LocalStorageService } from './local-storage.service';
import { SyncStateService } from './sync-state.service';

@Injectable({
  providedIn: 'root',
})
/**
 * Request service dispatch request between localstorage and server regarding the syncstate
 */
export class RequestService {
  private currentDb: string = 'cad42_dev_14';

  constructor(
    private localStorageService: LocalStorageService,
    private apiService: ApiService,
    private syncStateService: SyncStateService,
    private projectService: ProjectService,
  ) {
    this.projectService.currentProject.subscribe((project: Project | null) => {
      if (project) {
        this.syncStateService.deleteSyncState(this.currentDb);
        this.currentDb = project.database;
      }
    })
  }

  /**
   *
   * @returns currentDb
   */
  getCurrentDb(): any {
    return this.currentDb;
  }

  /**
   * Update the current db by checking the sync state
   * @param dbName
   * @returns
   */
  setCurrentDb(dbName: string): Observable<void> {
    if (this.currentDb != dbName) {
      this.currentDb = dbName;
      return this.syncStateService.getDbSyncStateFromServer(dbName);
    } else {
      return of();
    }
  }

  /**
   * Base de la clé de cache/syncState pour une collection : la base du projet
   * actif pour une collection 'tenant', une room globale fixe pour 'global'
   * (une collection globale ne doit pas voir son cache fragmenté par projet).
   */
  private dbScopeFor(collection: string): string {
    return dbScopeFor(collection, this.currentDb);
  }

  /**
   * Get data from a given collection
   * @param collection
   * @param path
   * @param query
   * @returns
   */
  getCollection(collection: string, collectionPath: string, query: any, path: string = ""): Observable<any> {
    const dbScope = this.dbScopeFor(collection);

    //TODO: handle query
    return this.syncStateService
      .getCollectionSyncState(dbScope, collection)
      .pipe(
        switchMap((syncState) => {
          if (!syncState[collection] || syncState[collection].state == 'expired' || !this.localStorageService.getItem(dbScope + '_' + collection)) {
            return this.apiService.getCollection(collectionPath, path, query).pipe(
              tap((data) => {
                this.syncStateService.updateSyncState(
                  dbScope,
                  collection,
                  moment().unix(),
                  'uptodate'
                );
                if (data) {
                  const filteredData = Array.isArray(data) && collection == "Model" ? data.map((item: any) => {
                    const { resizedImage, ...rest } = item;
                    return rest;
                  }) : data;

                  this.localStorageService.setItem(
                    dbScope + '_' + collection,
                    filteredData
                  );
                }
              })
            );
          } else {
            //it loads local data
            return of(
              this.localStorageService.getItem(
                dbScope + '_' + collection
              )
            );
          }
        })
      );
  }

  /**
   * Get a single item by ID
   * @param collection - The name of the collection
   * @param path - The API path for the collection
   * @param id - The ID of the item to retrieve
   * @returns An observable of the retrieved item
   */
  getItem(collection: string, collectionPath: string, id: string): Observable<any> {
    const dbScope = this.dbScopeFor(collection);

    return this.syncStateService
      .getCollectionSyncState(dbScope, collection)
      .pipe(
        switchMap((syncState) => {

          if (
            !syncState[collection] ||
            syncState[collection].state == 'expired'
          ) {

            return this.apiService.getCollection(collectionPath, id).pipe(
              tap((data) => {
                const localData = this.localStorageService.getItem(
                  `${dbScope}_${collection}`
                ) || [];
                const localDataArray = Array.isArray(localData) ? localData : [];
                this.localStorageService.setItem(
                  `${dbScope}_${collection}`,
                  [...localDataArray, data]
                );
              })
            );
          } else {

            const localData = this.localStorageService.getItem(
              `${dbScope}_${collection}`
            ) || [];
            const localDataArray = Array.isArray(localData) ? localData : [];

            const item = localDataArray.find((item: any) => item.id === id);
            return of(item || null);
          }
        })
      );
  }

  /**
  * Add a new item to a collection
  * @param collection - The name of the collection
  * @param path - The API path for the collection
  * @param item - The item to add
  * @returns An observable of the created item
  */
  postItem(collection: string, collectionPath: string, item: any, options: any = {}): Observable<any> {
    return this.apiService.postCollectionData(collectionPath, '', item, options).pipe(
      tap((data) => {
        this.syncStateService.updateSyncState(
          this.dbScopeFor(collection),
          collection,
          moment().unix(),
          'uptodate'
        );
      })
    );
  }

  /**
 * Add multiple items to a collection
 * @param collection - The name of the collection
 * @param collectionPath - The API path for the collection
 * @param items - The array of items to add
 * @returns An observable of the created items
 */
  postItems(collection: string, collectionPath: string, items: any[]): Observable<any[]> {
    return this.apiService.postCollectionData(collectionPath, '', items, {}).pipe(
      tap((data) => {
        this.syncStateService.updateSyncState(
          this.dbScopeFor(collection),
          collection,
          moment().unix(),
          'uptodate'
        );
      })
    );
  }

  /**
   * Filtre les données localement selon les paramètres de recherche
   */
  private filterLocalData(data: any[], query: any): any[] {
    return data.filter((item) => {
      return Object.keys(query).every((key) => {
        const value = query[key];

        if (Array.isArray(item[key])) {
          return item[key].includes(value);
        }

        return item[key] === value || item[key]?.toString().includes(value);
      });
    });
  }


  /**
   * Make PUT operation on a given collection
   * @param collection
   * @param path
   * @param item
   * @returns
   */
  setProjectData(collection: string, collectionPath: string, item: any): Observable<any> {
    return this.apiService.updateCollectionItem(collectionPath, item).pipe(
      tap((data) => {
        // Localstorage update
        this.syncStateService.updateSyncState(
          this.dbScopeFor(collection),
          collection,
          moment().unix(),
          'uptodate'
        );
      })
    );
  }

  /**
 * Make PUT operation on a given collection with id in body
 * @param collection
 * @param path
 * @param item
 * @returns
 */
  setProjectDataWithIdBody(collection: string, collectionPath: string, item: any): Observable<any> {
    return this.apiService.updateCollection(collectionPath, "", item).pipe(
      tap((data) => {
        // Localstorage update
        this.syncStateService.updateSyncState(
          this.dbScopeFor(collection),
          collection,
          moment().unix(),
          'uptodate'
        );
      })
    );
  }

  /**
  * Delete an item from a collection
  * @param collection - The name of the collection
  * @param path - The API path for the collection
  * @param id - The ID of the item to delete
  * @returns An observable of the deletion result
  */
  deleteItem(collection: string, collectionPath: string, id: string, params: any = {}): Observable<any> {
    const dbScope = this.dbScopeFor(collection);

    return this.apiService.deleteCollectionItem(collectionPath, id, params).pipe(
      tap(() => {
        const localData: any = this.localStorageService.getItem(`${dbScope}_${collection}`) || [];
        const updatedData = localData.filter((item: any) => item.id !== id);

        this.localStorageService.setItem(
          `${dbScope}_${collection}`,
          updatedData
        );

        this.syncStateService.updateSyncState(
          dbScope,
          collection,
          moment().unix(),
          'uptodate'
        );
      })
    );
  }

  deleteManyItems(collection: string, collectionPath: string, query: QueryParams): Observable<any> {
    const dbScope = this.dbScopeFor(collection);

    return this.apiService.deleteCollectionItems(collectionPath, query).pipe(
      tap(() => {
        const localData: any = this.localStorageService.getItem(
          `${dbScope}_${collection}`
        ) || [];
        let updatedData: any[] = [];
        Object.keys(query).forEach(key => {
          let property = key.charAt(key.length - 1) == "s" ? key.slice(0, -1) : key;
          let filtered = localData.filter((item: any) => {
            if (Array.isArray(query[key]))
              return !query[key].includes(item[property])
            return query[key] == item[property];
          });
          updatedData = updatedData.concat(filtered);
        });

        this.localStorageService.setItem(
          `${dbScope}_${collection}`,
          updatedData
        );

        this.syncStateService.updateSyncState(
          dbScope,
          collection,
          moment().unix(),
          'uptodate'
        );
      })
    );
  }

}
